import type { BounceReason } from '../types/api';

/**
 * `hard` blocks the address, `soft` is only recorded, `fix` is a hard
 * bounce worth correcting and `done` one already dealt with.
 */
export type BounceReasonKind = 'fix' | 'done' | 'hard' | 'soft' | 'spam';

export interface BounceReasonInfo {
  label: string;
  kind: BounceReasonKind;
  /** Shown above the list: what it means and what happens to the address. */
  text: string;
  /** Shown in an opened row: what this bounce did to the address. */
  effect: string;
  blocked: boolean;
}

/**
 * The reasons in the order of the side list (Possible typo first: the one
 * that can be fixed, then the typos already dealt with). What happens to the address follows
 * `apps/edge/src/routes/sns.ts`: a Permanent bounce and a complaint block
 * it; a Transient or Undetermined bounce is only recorded. No count of soft
 * bounces in a row exists, so none is promised here.
 */
export const BOUNCE_REASON_ORDER: BounceReason[] = [
  'typo',
  'corrected',
  'nodomain',
  'nobox',
  'suppressed',
  'full',
  'refused',
  'spam',
];

export const BOUNCE_REASON_INFO: Record<BounceReason, BounceReasonInfo> = {
  typo: {
    label: 'Possible typo',
    kind: 'fix',
    text: 'The domain looks misspelled. Ask the customer, or correct the address on the contact and it gets mail again.',
    effect:
      'Blocked: this address gets no more mail. Correct the address on the contact to mail it again.',
    blocked: true,
  },
  corrected: {
    label: 'Typo corrected',
    kind: 'done',
    text: 'The right address is a contact now: it was corrected here, or it was already on the list. Nothing left to do. The misspelled address stays blocked.',
    effect: 'Blocked: the misspelled address gets no more mail. The right address is mailed.',
    blocked: true,
  },
  nodomain: {
    label: 'Domain does not exist',
    kind: 'hard',
    text: 'Nobody receives mail on this domain. Blocked: we never mail it again.',
    effect: 'Blocked: this address gets no more mail.',
    blocked: true,
  },
  nobox: {
    label: 'Address does not exist',
    kind: 'hard',
    text: 'The server refused this address for good, usually because the mailbox is gone or never existed. Blocked: we never mail it again.',
    effect: 'Blocked: this address gets no more mail.',
    blocked: true,
  },
  suppressed: {
    label: 'Already on a block list',
    kind: 'hard',
    text: 'Already on the sending service’s block list from earlier. Blocked.',
    effect: 'Blocked: this address gets no more mail.',
    blocked: true,
  },
  full: {
    label: 'Mailbox full',
    kind: 'soft',
    text: 'Temporary. The address is still mailed with the next mail. Repeated soft bounces do not block it automatically.',
    effect: 'Still mailed: a soft bounce does not block the address.',
    blocked: false,
  },
  refused: {
    label: 'Refused by server',
    kind: 'soft',
    text: 'The receiving server said no this time (busy, filter, greylisting) or gave no clear answer. Still mailed: nothing is blocked.',
    effect: 'Still mailed: this bounce does not block the address.',
    blocked: false,
  },
  spam: {
    label: 'Marked as spam',
    kind: 'spam',
    text: 'The person pressed “Report spam”. Blocked. If 0.1% of a mail’s recipients do this (counted from 20 sent), that send is paused automatically.',
    effect: 'Blocked: this address gets no more mail.',
    blocked: true,
  },
};

/** SES's bounce sub-types (SES developer guide, "Bounce types"), for the opened row. */
export const SUB_TYPE_LABELS: Record<string, string> = {
  General: 'General',
  NoEmail: 'No such address',
  Suppressed: 'On the AWS suppression list',
  OnAccountSuppressionList: 'On the AWS suppression list',
  MailboxFull: 'Mailbox full',
  MessageTooLarge: 'Mail too large',
  ContentRejected: 'Content refused',
  AttachmentRejected: 'Attachment refused',
};
